export interface MockCenter {
  id: number;
  name: string;
  code: string;
  address: string;
  city: string;
  state: string;
  phone: string;
  email: string;
  is_active: boolean;
}

export interface MockAvailabilitySlot {
  id: number;
  center_id: number;
  date: string;
  start_time: string;
  end_time: string;
  capacity: number;
  booked: number;
  status: 'AVAILABLE' | 'FULL' | 'CLOSED';
}

export interface MockBooking {
  id: number;
  applicant_id: number;
  slot_id: number;
  status: 'CONFIRMED' | 'CANCELLED' | 'COMPLETED';
  booking_reference: string;
  created_at: string;
  updated_at: string;
  cancelled_at?: string;
  
  // Joined data
  slot?: MockAvailabilitySlot;
  center?: MockCenter;
}

export const mockCenters: MockCenter[] = [
  {
    id: 1,
    name: "NCAA Abuja - Corporate Headquarters",
    code: "ABV-HQ",
    address: "NCAA Corporate Headquarters, Nnamdi Azikiwe Airport",
    city: "Abuja",
    state: "FCT",
    phone: "+234-800-NCAA-001",
    email: "abuja.hq@ncaa.gov.ng",
    is_active: true
  },
  {
    id: 2,
    name: "NCAA Lagos Regional Office",
    code: "LOS-RO",
    address: "NCAA Lagos Regional Office, Murtala Muhammed Airport, Ikeja",
    city: "Lagos",
    state: "Lagos",
    phone: "+234-800-NCAA-002",
    email: "lagos.regional@ncaa.gov.ng",
    is_active: true
  },
  {
    id: 3,
    name: "NCAA Kano Regional Office",
    code: "KAN-RO",
    address: "NCAA Regional Office, Mallam Aminu Kano International Airport",
    city: "Kano",
    state: "Kano",
    phone: "+234-800-NCAA-003",
    email: "kano.regional@ncaa.gov.ng",
    is_active: true
  },
  {
    id: 4,
    name: "NCAA Port Harcourt Regional Office",
    code: "PHC-RO",
    address: "NCAA Regional Office, Port Harcourt International Airport",
    city: "Port Harcourt",
    state: "Rivers",
    phone: "+234-800-NCAA-004",
    email: "portharcourt.regional@ncaa.gov.ng",
    is_active: true
  }
];

// Get next 30 days for slot generation
const getNextDays = (count: number): string[] => {
  const dates: string[] = [];
  const today = new Date();
  
  for (let i = 1; i <= count; i++) {
    const date = new Date(today);
    date.setDate(today.getDate() + i);
    dates.push(date.toISOString().split('T')[0]);
  }
  
  return dates;
};

// Generate 5 slots per center for next 2 days
const generateSlots = (): MockAvailabilitySlot[] => {
  const slots: MockAvailabilitySlot[] = [];
  const dates = getNextDays(2);
  const times = [
    { start: '09:00', end: '10:00' },
    { start: '10:30', end: '11:30' },
    { start: '12:00', end: '13:00' },
    { start: '14:00', end: '15:00' },
    { start: '15:30', end: '16:30' }
  ];
  
  let slotId = 1;
  
  mockCenters.forEach((center) => {
    dates.forEach((date) => {
      times.forEach((time) => {
        const booked = Math.floor(Math.random() * 3); // 0-2 bookings
        const capacity = 5;
        slots.push({
          id: slotId++,
          center_id: center.id,
          date,
          start_time: time.start,
          end_time: time.end,
          capacity,
          booked,
          status: booked >= capacity ? 'FULL' : 'AVAILABLE'
        });
      });
    });
  });
  
  return slots;
};

export const mockSlots = generateSlots();
export const mockTimeSlots = mockSlots; // Alias for BookingCenter component

export const mockBookings: MockBooking[] = [
  {
    id: 1,
    applicant_id: 1,
    slot_id: 1,
    status: 'CONFIRMED',
    booking_reference: 'BK-20250109-A1B2C3D4',
    created_at: '2025-01-02T10:30:00Z',
    updated_at: '2025-01-02T10:30:00Z',
    slot: { ...mockSlots[0] },
    center: mockCenters[0]
  },
  {
    id: 2,
    applicant_id: 2,
    slot_id: 6,
    status: 'CONFIRMED',
    booking_reference: 'BK-20250110-E5F6G7H8',
    created_at: '2025-01-03T14:15:00Z',
    updated_at: '2025-01-03T14:15:00Z',
    slot: { ...mockSlots[5] },
    center: mockCenters[1]
  },
  {
    id: 3,
    applicant_id: 1,
    slot_id: 15,
    status: 'COMPLETED',
    booking_reference: 'BK-20241220-I9J0K1L2',
    created_at: '2024-12-15T09:00:00Z',
    updated_at: '2024-12-20T11:00:00Z',
    slot: {
      id: 15,
      center_id: 1,
      date: '2024-12-20',
      start_time: '10:00',
      end_time: '11:00',
      capacity: 5,
      booked: 5,
      status: 'FULL' as const
    },
    center: mockCenters[0]
  },
  {
    id: 4,
    applicant_id: 3,
    slot_id: 20,
    status: 'CANCELLED',
    booking_reference: 'BK-20250105-M3N4O5P6',
    created_at: '2024-12-28T16:20:00Z',
    updated_at: '2025-01-04T08:30:00Z',
    cancelled_at: '2025-01-04T08:30:00Z',
    slot: { ...mockSlots[19] },
    center: mockCenters[2]
  },
  {
    id: 5,
    applicant_id: 4,
    slot_id: 25,
    status: 'CONFIRMED',
    booking_reference: 'BK-20250111-Q7R8S9T0',
    created_at: '2025-01-05T11:45:00Z',
    updated_at: '2025-01-05T11:45:00Z',
    slot: { ...mockSlots[24] },
    center: mockCenters[3]
  }
];

// Helper to get bookings for current user
export const getMyBookings = (): MockBooking[] => {
  return mockBookings.filter(b => b.applicant_id === 1);
};

// Helper to get available slots
export const getAvailableSlots = (centerId?: number, date?: string): MockAvailabilitySlot[] => {
  let filtered = mockSlots.filter(s => s.booked < s.capacity);
  
  if (centerId) {
    filtered = filtered.filter(s => s.center_id === centerId);
  }
  
  if (date) {
    filtered = filtered.filter(s => s.date === date);
  }
  
  return filtered;
};
